import React, { useState } from 'react';
import { Box, Collapse, Grid, Link, Typography, useTheme } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { get } from 'lodash';
import CustomerPopupModal from '../../components/form/CustomerPopupModal';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const ProfileCard = ({ title, subtitle, document, rawData, onUpdate, readOnly }) => {
  const theme = useTheme();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const { eInvoiceForm } = useTranslatedMessages();
  const { profile_card: t } = eInvoiceForm;
  const handleClose = () => setOpen(false);

  const formatAddress = (prefix) => {
    const parts = [
      get(document, `${prefix}street_name`),
      get(document, `${prefix}additional_street_name`),
      get(document, `${prefix}address_line`),
      get(document, `${prefix}city_name`),
      get(document, `${prefix}country_subentity`),
      get(document, `${prefix}postal_zone`),
    ]?.filter(Boolean);

    return parts.join(', ');
  };

  const formatPeppolId = () => {
    const schemeId = document?.endpoint_scheme_id;
    const endpointId = document?.endpoint_id;

    if (schemeId == null || String(schemeId).trim() === '' || endpointId == null || String(endpointId).trim() === '') {
      return '-';
    }

    return `${String(schemeId).trim()}:${String(endpointId).trim()}`;
  };

  const presentValue = (value) => {
    if (value == null || String(value).trim() === '') {
      return null;
    }

    return String(value).trim();
  };

  const businessName = presentValue(get(document, 'party_name')) || presentValue(get(document, 'name'));
  const vatValue = presentValue(get(document, 'party_tax_scheme.company_id'));
  const tinValue = presentValue(get(document, 'tin'));
  const companyId = presentValue(get(document, 'company_id'));

  const summaryParts = [businessName, vatValue, tinValue, companyId].filter(Boolean);

  const fields = [
    { id: 'contactPerson', label: t.fields.contact_person, key: 'name' },
    { id: 'businessName', label: t.fields.business_name, key: 'party_name' },
    { id: 'address', label: t.fields.address, key: 'postal_address' },
    { id: 'country', label: t.fields.country, key: 'postal_address.country_code' },
    { id: 'phone', label: t.fields.phone, key: 'telephone' },
    { id: 'email', label: t.fields.email, key: 'email' },
    {
      id: 'vat',
      label: t.fields.vat,
      key: 'party_tax_scheme.company_id',
    },
    { id: 'tinTrn', label: t.fields.tin, key: 'tin' },
    ...(document?.scheme_agency_id ? [{ id: 'companyId', label: t.fields.company_id, key: 'company_id' }] : []),
    { id: 'peppolId', label: t.fields.peppol_id, getValue: formatPeppolId },
  ];

  return (
    <>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: 'inherit',
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 0.75 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              fontSize: '1rem',
            }}
          >
            {subtitle || title}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1.5,
            rowGap: 0.75,
          }}
        >
          {summaryParts.map((part, index) => (
            <React.Fragment key={`${part}-${index}`}>
              {index > 0 ? (
                <Box
                  aria-hidden
                  sx={{
                    alignSelf: 'stretch',
                    width: '2px',
                    backgroundColor: 'text.primary',
                    opacity: 0.35,
                  }}
                />
              ) : null}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  lineHeight: 1,
                  wordBreak: 'break-word',
                }}
              >
                {part}
              </Typography>
            </React.Fragment>
          ))}
        </Box>

        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => setDetailsOpen((prev) => !prev)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 0.25,
            mt: 0.75,
            color: 'primary.main',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            p: 0,
            font: 'inherit',
          }}
        >
          {detailsOpen ? (t.buttons?.hide_details ?? 'Hide details') : (t.buttons?.view_details ?? 'View details')}
          {detailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Link>

        <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1.25),
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {fields.map((field) => (
              <Grid container key={field.id || field.label}>
                <Grid fontSize="0.90rem" size={{ xs: 4 }} sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {field.label}:
                </Grid>
                <Grid
                  size={{ xs: 5 }}
                  fontSize="0.90rem"
                  sx={{
                    maxWidth: 300,
                    wordWrap: 'break-word',
                    marginLeft: theme.spacing(1),
                    fontWeight: 400,
                    color: 'text.secondary',
                  }}
                  style={
                    field.id === 'address'
                      ? {
                          direction: 'ltr',
                          unicodeBidi: 'isolate',
                          textAlign: 'left',
                        }
                      : undefined
                  }
                >
                  {typeof field.getValue === 'function'
                    ? field.getValue()
                    : field.id === 'address'
                      ? formatAddress(field.key ? `${field.key}.` : '') || '-'
                      : String(get(document, field.key || 'defaultKey') || '-')}
                </Grid>
              </Grid>
            ))}
          </Box>
        </Collapse>

        <Box sx={{ flex: 1 }} />
      </Box>
      {open && (
        <CustomerPopupModal
          open={open}
          handleClose={handleClose}
          document={document}
          onSave={onUpdate}
          setError={() => {}}
        />
      )}
    </>
  );
};

export default ProfileCard;
